import {
  cloneElement,
  isValidElement,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useDrawerHistory } from '@/hooks/use-drawer-history';
import type { VariantProps } from 'class-variance-authority';
import { XIcon } from 'lucide-react';

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHandle,
  DrawerHeader,
  DrawerNestedRoot,
  DrawerTitle,
  DrawerTrigger,
} from '@/lib/shadcn/components/ui/drawer';
import { Stack, stackVariants } from '@/lib/shadcn/components/ui/stack/stack';
import { cn } from '@/lib/shadcn/lib/utils';

import {
  BaseDrawerStackContext,
  type BaseDrawerView,
  useBaseDrawerStack,
} from './base-drawer-context';

export interface BaseDrawerProps extends PropsWithChildren {
  onCloseAutoFocus?: (event: Event) => void;
  nested?: boolean;
  disablePreventScroll?: boolean;
  disableHistory?: boolean;
  noBodyStyles?: boolean;
  shouldScaleBackground?: boolean;
  title: React.ReactNode;
  description?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  snapPoints?: number[];
  className?: string;
  overlayClassName?: string;
  trigger: React.ReactNode;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  footerContent?: React.ReactNode;
  showButtonClose?: boolean;
  drawerFooterClassName?: string;
  handleOnly?: boolean;
  headerClassName?: string;
  contentGap?: VariantProps<typeof stackVariants>['gap'];
}

export function BaseDrawer({
  children,
  nested = false,
  disablePreventScroll,
  noBodyStyles,
  shouldScaleBackground,
  onCloseAutoFocus,
  title,
  description,
  isOpen,
  onOpenChange,
  snapPoints,
  className,
  overlayClassName,
  trigger,
  primaryAction,
  secondaryAction,
  footerContent,
  showButtonClose = true,
  drawerFooterClassName,
  handleOnly = true,
  headerClassName,
  contentGap = 0,
  disableHistory,
}: BaseDrawerProps) {
  const { t } = useTranslation();
  const parentStack = useBaseDrawerStack();
  const viewId = useId();
  const wasRegisteredRef = useRef(false);

  // If this drawer is mounted inside another BaseDrawer, participate in the parent stack.
  // We still hook into useDrawerHistory so that mobile back gestures/buttons pop this specific view!
  const historyProps = useDrawerHistory({
    open: isOpen,
    onOpenChange,
    disableHistory,
  });

  const effectiveOpen = parentStack ? historyProps.open : isOpen;
  const effectiveOnOpenChange = parentStack ? historyProps.onOpenChange : onOpenChange;

  useLayoutEffect(() => {
    if (!parentStack) return;

    if (effectiveOpen) {
      wasRegisteredRef.current = true;
      parentStack.registerView({
        id: viewId,
        title,
        description,
        children,
        primaryAction,
        secondaryAction,
        footerContent,
        showButtonClose,
        drawerFooterClassName,
        handleOnly,
        headerClassName,
        contentGap,
        className,
        overlayClassName,
        onClose: () => effectiveOnOpenChange?.(false),
        onOpenChange: effectiveOnOpenChange,
        onCloseAutoFocus,
      });
    } else if (wasRegisteredRef.current) {
      wasRegisteredRef.current = false;
      parentStack.unregisterView(viewId);
    }
  }, [
    parentStack,
    effectiveOpen,
    effectiveOnOpenChange,
    viewId,
    title,
    description,
    children,
    primaryAction,
    secondaryAction,
    footerContent,
    showButtonClose,
    drawerFooterClassName,
    handleOnly,
    headerClassName,
    contentGap,
    className,
    overlayClassName,
    onCloseAutoFocus,
  ]);

  useEffect(() => {
    return () => {
      if (wasRegisteredRef.current) {
        parentStack?.unregisterView(viewId);
      }
    };
  }, [parentStack, viewId]);

  if (parentStack) {
    if (trigger) {
      if (isValidElement(trigger)) {
        return cloneElement(
          trigger as React.ReactElement<{
            onClick?: (e: MouseEvent) => void;
          }>,
          {
            onClick: (e: MouseEvent) => {
              (
                trigger.props as {
                  onClick?: (e: MouseEvent) => void;
                }
              )?.onClick?.(e);
              effectiveOnOpenChange?.(true);
            },
          },
        );
      }
      return (
        <span
          role="button"
          tabIndex={0}
          onClick={() => effectiveOnOpenChange?.(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              effectiveOnOpenChange?.(true);
            }
          }}
        >
          {trigger}
        </span>
      );
    }
    return null;
  }

  return (
    <RootBaseDrawer
      nested={nested}
      disablePreventScroll={disablePreventScroll}
      noBodyStyles={noBodyStyles}
      shouldScaleBackground={shouldScaleBackground}
      onCloseAutoFocus={onCloseAutoFocus}
      title={title}
      description={description}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      snapPoints={snapPoints}
      className={className}
      overlayClassName={overlayClassName}
      trigger={trigger}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      footerContent={footerContent}
      showButtonClose={showButtonClose}
      drawerFooterClassName={drawerFooterClassName}
      handleOnly={handleOnly}
      headerClassName={headerClassName}
      contentGap={contentGap}
      t={t}
    >
      {children}
    </RootBaseDrawer>
  );
}

function RootBaseDrawer({
  children,
  nested = false,
  disablePreventScroll,
  noBodyStyles,
  shouldScaleBackground,
  onCloseAutoFocus,
  title,
  description,
  isOpen,
  onOpenChange,
  snapPoints,
  className,
  overlayClassName,
  trigger,
  primaryAction,
  secondaryAction,
  footerContent,
  showButtonClose = true,
  drawerFooterClassName,
  handleOnly = true,
  headerClassName,
  contentGap = 0,
  t,
}: Omit<BaseDrawerProps, 'disableHistory'> & { t: (key: string) => string }) {
  const [subViews, setSubViews] = useState<BaseDrawerView[]>([]);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const prevHeightRef = useRef<number | null>(null);
  const prevViewIdRef = useRef<string>('__root__');
  const activeAnimRef = useRef<Animation | null>(null);

  const registerView = useCallback((newView: BaseDrawerView) => {
    if (drawerRef.current) {
      prevHeightRef.current = drawerRef.current.offsetHeight;
    }
    setSubViews((prev) => {
      const idx = prev.findIndex((v) => v.id === newView.id);
      if (idx !== -1) {
        const copy = [...prev];
        copy[idx] = newView;
        return copy;
      }
      return [...prev, newView];
    });
  }, []);

  const unregisterView = useCallback((id: string) => {
    if (drawerRef.current) {
      prevHeightRef.current = drawerRef.current.offsetHeight;
    }
    setSubViews((prev) => prev.filter((v) => v.id !== id));
  }, []);

  const stackContextValue = useMemo(
    () => ({ registerView, unregisterView }),
    [registerView, unregisterView],
  );

  const rootView = useMemo<BaseDrawerView>(
    () => ({
      id: '__root__',
      title,
      description,
      children,
      primaryAction,
      secondaryAction,
      footerContent,
      showButtonClose,
      drawerFooterClassName,
      handleOnly,
      headerClassName,
      contentGap,
      className,
      overlayClassName,
      onClose: () => onOpenChange?.(false),
      onOpenChange,
      onCloseAutoFocus,
    }),
    [
      title,
      description,
      children,
      primaryAction,
      secondaryAction,
      footerContent,
      showButtonClose,
      drawerFooterClassName,
      handleOnly,
      headerClassName,
      contentGap,
      className,
      overlayClassName,
      onOpenChange,
      onCloseAutoFocus,
    ],
  );

  const hasSubViews = subViews.length > 0;
  const activeView = (hasSubViews ? subViews.at(-1) : null) ?? rootView;
  const hasFooter = Boolean(
    activeView.primaryAction || activeView.secondaryAction || activeView.footerContent,
  );

  // Fluid height scaling between drawers
  useLayoutEffect(() => {
    const drawer = drawerRef.current;
    if (!drawer) return;

    const isViewChange = prevViewIdRef.current !== activeView.id;
    prevViewIdRef.current = activeView.id;

    if (!isViewChange) {
      prevHeightRef.current = drawer.offsetHeight;
      return;
    }

    const prevHeight = prevHeightRef.current;
    const newHeight = drawer.offsetHeight;
    prevHeightRef.current = newHeight;

    if (prevHeight && newHeight && Math.abs(prevHeight - newHeight) > 2) {
      if (activeAnimRef.current) {
        activeAnimRef.current.cancel();
      }

      drawer.style.clipPath = 'inset(0)';

      const anim = drawer.animate(
        [{ height: `${prevHeight}px` }, { height: `${newHeight}px` }],
        {
          duration: 280,
          easing: 'cubic-bezier(0.32, 0.72, 0, 1)',
        },
      );
      activeAnimRef.current = anim;
      anim.onfinish = () => {
        if (drawerRef.current) {
          drawerRef.current.style.clipPath = '';
        }
        activeAnimRef.current = null;
      };
    }

    return () => {
      if (activeAnimRef.current) {
        activeAnimRef.current.cancel();
        activeAnimRef.current = null;
      }
      if (drawerRef.current) {
        drawerRef.current.style.clipPath = '';
      }
    };
  }, [activeView.id]);

  const Root = nested ? DrawerNestedRoot : Drawer;

  return (
    <BaseDrawerStackContext.Provider value={stackContextValue}>
      <Root
        snapPoints={snapPoints}
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            if (subViews.length > 0) {
              const topView = subViews.at(-1);
              topView?.onClose?.();
              if (drawerRef.current) {
                drawerRef.current.style.transform = 'translate3d(0, 0, 0)';
                drawerRef.current.style.transition =
                  'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)';
                setTimeout(() => {
                  if (drawerRef.current) {
                    drawerRef.current.style.transition = '';
                  }
                }, 300);
              }
              return;
            }
            setSubViews([]);
            prevHeightRef.current = null;
          }
          onOpenChange?.(open);
        }}
        handleOnly={activeView.handleOnly ?? handleOnly}
        disablePreventScroll={disablePreventScroll ?? true}
        noBodyStyles={noBodyStyles ?? true}
        shouldScaleBackground={shouldScaleBackground ?? false}
        disableHistory
      >
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent
          ref={drawerRef}
          className={cn(
            'overflow-visible border-none',
            className,
            hasSubViews && activeView.className,
          )}
          overlayClassName={activeView.overlayClassName || overlayClassName}
          data-testid="base-drawer"
          onCloseAutoFocus={activeView.onCloseAutoFocus || onCloseAutoFocus}
        >
          <Stack gap={activeView.contentGap ?? contentGap} className="min-h-0 flex-1">
            <DrawerHeader
              className={cn(
                'flex-col items-center gap-0',
                activeView.headerClassName || headerClassName,
              )}
            >
              <DrawerHandle className="my-unit-4" />
              <Stack direction="row" align="center" justify="between" className="w-full">
                <DrawerTitle className="text-wrap text-left" data-testid="modal-title">
                  {activeView.title}
                </DrawerTitle>
                {activeView.showButtonClose ? (
                  hasSubViews ? (
                    <button
                      type="button"
                      data-vaul-no-drag
                      className="flex size-10 shrink-0 select-none items-center justify-center rounded-medium bg-slate-100 text-slate-500 transition-colors [touch-action:manipulation] hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:bg-slate-250"
                      data-testid="close-button"
                      aria-label={t('landing.overlays.close')}
                      onClick={() => {
                        activeView.onClose?.();
                      }}
                    >
                      <XIcon className="size-5 text-slate-500" />
                    </button>
                  ) : (
                    <DrawerClose
                      data-vaul-no-drag
                      className="flex size-10 shrink-0 select-none items-center justify-center rounded-medium bg-slate-100 text-slate-500 transition-colors [touch-action:manipulation] hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:bg-slate-250"
                      data-testid="close-button"
                      aria-label={t('landing.overlays.close')}
                    >
                      <XIcon className="size-5 text-slate-500" />
                    </DrawerClose>
                  )
                ) : null}
              </Stack>
            </DrawerHeader>
            {activeView.description ? (
              <DrawerDescription className="sr-only">
                {activeView.description}
              </DrawerDescription>
            ) : null}

            <Stack
              data-vaul-no-drag
              className={cn(
                'size-full overflow-y-auto px-unit-8',
                hasFooter ? 'pb-0' : 'pb-[calc(16px+env(safe-area-inset-bottom,0px))]',
              )}
            >
              {/* Keep root view mounted so nested triggers remain active */}
              <div
                className={cn(
                  'size-full min-h-0 flex-col',
                  hasSubViews ? 'hidden' : 'animate-drawer-fade-in flex',
                )}
              >
                {children}
              </div>

              {/* Sub-views body */}
              {subViews.map((v, index) => {
                const isTop = index === subViews.length - 1;
                return (
                  <div
                    key={v.id}
                    className={cn(
                      'size-full min-h-0 flex-col',
                      isTop ? 'animate-drawer-fade-in flex' : 'hidden',
                    )}
                  >
                    {v.children}
                  </div>
                );
              })}
            </Stack>
          </Stack>
          {hasFooter ? (
            <DrawerFooter
              data-vaul-no-drag
              className={cn(
                'w-full gap-unit-2 border-t border-slate-100 bg-transparent px-unit-8 pb-[calc(16px+env(safe-area-inset-bottom,0px))] pt-unit-4',
                activeView.footerContent && 'flex-col',
                activeView.drawerFooterClassName || drawerFooterClassName,
              )}
            >
              {activeView.footerContent}
              {activeView.footerContent &&
              (activeView.primaryAction || activeView.secondaryAction) ? (
                <Stack direction="row" gap={2} className="w-full">
                  {activeView.secondaryAction}
                  {activeView.primaryAction}
                </Stack>
              ) : null}
              {activeView.footerContent ? null : activeView.secondaryAction}
              {activeView.footerContent ? null : activeView.primaryAction}
            </DrawerFooter>
          ) : null}
        </DrawerContent>
      </Root>
    </BaseDrawerStackContext.Provider>
  );
}
